/**
 * NavigationPage.jsx — Redesigned Sidebar Navigation
 *
 * Architecture:
 *  - <Sidebar>          Collapsible shell (expanded ↔ icon-only)
 *    - <BrandBar>       Logo + collapse toggle
 *    - <UserCard>       Avatar, name, role, online indicator
 *    - <SearchTrigger>  Cmd+K style filter
 *    - <NavScroll>      Scrollable nav area
 *      - <NavSection>   Collapsible labeled group
 *        - <NavItem>    Individual leaf item
 *  - <Content>          Topbar + page outlet
 *
 * Role gating is preserved exactly as-is.
 * No routing/auth logic was changed.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";
import { auth } from "../firebaseConfig";
import { signOut } from "firebase/auth";
import { useSetRecoilState } from "recoil";
import { authAtom } from "../App.js";
import logo from "../assets/COT_logo-removebg-preview.png";
import { useNavigate } from "react-router-dom";

import Main from "../../src/pages/Main.js";
import EditInvoice from "./EditInvoice.js";
import SearchInvoice from "./SearchInvoice.js";
import TransportSearch from "./TransportSearch.js";
import HotelSearch from "./HotelSearch.js";
import FlightSearch from "./FlightSearch.js";
import SupplierLedger from "./SupplierLedger.js";
import ManageAgents from "./ManageAgents.js";
import FolderReport from "./FolderReport.js";
import FolderLedger from "./FolderLedger.js";
import CustomerRefundLedger from "./CustomerRefundLedger.js";
import InvoicedLedger from "./InvoicedLedger.js";
import CreditsManage from "./CreditsManage.js";
import FlightSupplierLedger from "./FlightSupplierLedger.js";
import VisaSupplierLedger from "./VisaSupplierLedger.js";
import HotelSupplierLedger from "./HotelSupplierLedger.js";
import TransportSupplierLedger from "./TransportSupplierLedger.js";
import IssuanceRequest from "./IssuanceRequest.js";
import PaymentConfirmationPage from "./PaymentConfirmationPage.js";
import PaymentRequestPage from "./PaymentRequestPage.js";
import Issuance from "./Issuance.js";
import CustomerRefundRequest from "./CustomerRefundRequest.js";
import RefundProcessing from "./RefundProcessing";

// ─────────────────────────────────────────────────────────────────────────────
// Constants (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────

const KOMAIL = "komail@crossoceantravel.co.uk";
const VISA_ONLY = "ooo@crossoceantravel.co.uk";
const AGENTS = [
  "iftikhar@crossoceantravel.co.uk",
  "rafey@crossoceantravel.co.uk",
  "talha@crossoceantravel.co.uk",
  "asad@crossoceantravel.co.uk",
  "israr@crossoceantravel.co.uk",
  "raza@crossoceantravel.co.uk",
  "tahir@crossoceantravel.co.uk",
  "waleed@crossoceantravel.co.uk",
  
];

// ─────────────────────────────────────────────────────────────────────────────
// Nav structure — each section has: id, label, icon (emoji/symbol), items[]
// ─────────────────────────────────────────────────────────────────────────────

const NAV_SECTIONS = {
  invoices: {
    id: "invoices",
    label: "Invoices",
    icon: "⬡",
    adminOnly: true,
    items: [
      { id: "Make New Invoice", label: "New Invoice",    icon: "⊕", shortcut: "N" },
      { id: "Edit Invoice",     label: "Edit Invoice",   icon: "✎" },
      { id: "Search Invoice",   label: "Search Invoice", icon: "⌕" },
    ],
  },
  schedules: {
    id: "schedules",
    label: "Schedules",
    icon: "◷",
    items: [
      { id: "Search Flight",    label: "Flights",    icon: "✈" },
      { id: "Search Hotel",     label: "Hotels",     icon: "⌂" },
      { id: "Search Transport", label: "Transport",  icon: "⊡" },
    ],
  },
  ledgers: {
    id: "ledgers",
    label: "Supplier Ledgers",
    icon: "⊟",
    adminOnly: true,
    items: {
      all: [
        { id: "Flight Supplier Ledger",  label: "Flight Ledger",    icon: "✈" },
        { id: "HotelSupplierLedger",     label: "Hotel Ledger",     icon: "⌂" },
        { id: "VisaSupplierLedger",      label: "Visa Ledger",      icon: "⊛" },
        { id: "TransportSupplierLedger", label: "Transport Ledger", icon: "⊡" },
      ],
      komail:   [{ id: "TransportSupplierLedger", label: "Transport Ledger", icon: "⊡" }],
      visaOnly: [{ id: "VisaSupplierLedger",      label: "Visa Ledger",      icon: "⊛" }],
    },
  },
  issuanceSubmission: {
    id: "issuanceSubmission",
    label: "Requests",
    icon: "◉",
    items: [
      { id: "IssuanceRequest",       label: "Issuance Request", icon: "⊕" },
      { id: "CustomerRefundRequest", label: "Refund Request",   icon: "↩" },
      { id: "PaymentRequestPage",    label: "Payment Request",  icon: "◈" },
    ],
  },
  processing: {
    id: "processing",
    label: "Processing",
    icon: "⚙",
    adminOnly: true,
    items: [
      { id: "Issuance",               label: "Issuance Process",  icon: "◉" },
      { id: "RefundProcessing",       label: "Refund Process",    icon: "↩" },
      { id: "PaymentConfirmationPage",label: "Payment Process",   icon: "◈" },
    ],
  },
  operations: {
    id: "operations",
    label: "Operations",
    icon: "⊞",
    adminOnly: true,
    items: [
      { id: "CreditsManage",        label: "Credits",          icon: "◈" },
      { id: "Manage Agents",        label: "Manage Agents",    icon: "⊕" },
      { id: "CustomerRefundLedger", label: "Customer Refunds", icon: "↩" },
      { id: "FolderLedger",         label: "Folder Calc",      icon: "⊞" },
      { id: "InvoicedLedger",       label: "Invoiced Ledger",  icon: "≡"  },
      { id: "FolderReport",         label: "Folder Report",    icon: "⊟" },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Page renderer (unchanged logic)
// ─────────────────────────────────────────────────────────────────────────────

const renderPage = (activeTab) => {
  const map = {
    "Make New Invoice":        <Main />,
    "Edit Invoice":            <EditInvoice />,
    "Search Invoice":          <SearchInvoice />,
    "Search Flight":           <FlightSearch />,
    "Search Hotel":            <HotelSearch />,
    "Search Transport":        <TransportSearch />,
    "Supplier Ledger":         <SupplierLedger />,
    "Manage Agents":           <ManageAgents />,
    "CreditsManage":           <CreditsManage />,
    "FolderLedger":            <FolderLedger />,
    "CustomerRefundLedger":    <CustomerRefundLedger />,
    "InvoicedLedger":          <InvoicedLedger />,
    "IssuanceRequest":         <IssuanceRequest />,
    "CustomerRefundRequest":   <CustomerRefundRequest />,
    "RefundProcessing":        <RefundProcessing />,
    "Issuance":                <Issuance />,
    "FolderReport":            <FolderReport />,
    "Flight Supplier Ledger":  <FlightSupplierLedger />,
    "VisaSupplierLedger":      <VisaSupplierLedger />,
    "HotelSupplierLedger":     <HotelSupplierLedger />,
    "TransportSupplierLedger": <TransportSupplierLedger />,
    "PaymentConfirmationPage": <PaymentConfirmationPage />,
    "PaymentRequestPage":      <PaymentRequestPage />,
  };
  return map[activeTab] ?? <Main />;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const getInitials = (email = "") =>
  email.split("@")[0].slice(0, 2).toUpperCase();

const getRole = (email) => {
  if (email === KOMAIL) return "Transport";
  if (email === VISA_ONLY) return "Visa";
  if (AGENTS.includes(email)) return "Agent";
  return "Admin";
};

const getDisplayName = (email = "") =>
  email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Flatten all items for search
const flattenAllItems = () =>
  Object.values(NAV_SECTIONS).flatMap((s) => {
    const items = Array.isArray(s.items) ? s.items : s.items?.all ?? [];
    return items.map((item) => ({ ...item, section: s.label }));
  });

// Get page label for breadcrumb
const getPageLabel = (id) =>
  flattenAllItems().find((i) => i.id === id)?.label ?? id;

// ─────────────────────────────────────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────────────────────────────────────

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseDot = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 #22c55e55; }
  50%       { box-shadow: 0 0 0 4px #22c55e11; }
`;

const sidebarExpand = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Global styles
// ─────────────────────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  :root {
    /* Sidebar palette */
    --sb-bg:          #0a0f1a;
    --sb-surface:     #0f1625;
    --sb-border:      rgba(255,255,255,0.06);
    --sb-text-muted:  #3d5270;
    --sb-text-dim:    #5a7399;
    --sb-text-base:   #8ba3c0;
    --sb-text-bright: #d4e0ef;
    --sb-accent:      #3b82f6;
    --sb-accent-glow: rgba(59,130,246,0.18);
    --sb-active-bg:   rgba(59,130,246,0.10);
    --sb-hover-bg:    rgba(255,255,255,0.04);
    --sb-section-gap: 4px;
    --sb-radius:      8px;

    /* Widths */
    --sb-w-expanded: 256px;
    --sb-w-collapsed: 60px;
    --sb-transition: 260ms cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Layout wrappers
// ─────────────────────────────────────────────────────────────────────────────

const Layout = styled.div`
  display: flex;
  height: 100vh;
  background: #f0f4f8;
  font-family: 'Geist', 'DM Sans', system-ui, sans-serif;
  overflow: hidden;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar shell
// ─────────────────────────────────────────────────────────────────────────────

const SidebarShell = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--sb-bg);
  width: ${({ $collapsed }) => $collapsed ? "var(--sb-w-collapsed)" : "var(--sb-w-expanded)"};
  min-width: ${({ $collapsed }) => $collapsed ? "var(--sb-w-collapsed)" : "var(--sb-w-expanded)"};
  transition: width var(--sb-transition), min-width var(--sb-transition);
  overflow: hidden;
  z-index: 100;

  /* Right edge glow */
  &::after {
    content: '';
    position: absolute;
    right: 0; top: 0; bottom: 0;
    width: 1px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      var(--sb-border) 20%,
      var(--sb-border) 80%,
      transparent 100%
    );
  }

  /* Mobile: drawer overlay */
  @media (max-width: 768px) {
    position: fixed;
    left: 0; top: 0;
    transform: ${({ $mobileOpen }) => $mobileOpen ? "translateX(0)" : "translateX(-100%)"};
    box-shadow: ${({ $mobileOpen }) => $mobileOpen ? "4px 0 40px rgba(0,0,0,0.5)" : "none"};
    width: var(--sb-w-expanded) !important;
    min-width: var(--sb-w-expanded) !important;
    z-index: 999;
  }
`;

const MobileOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $visible }) => $visible ? "block" : "none"};
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 998;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Brand bar
// ─────────────────────────────────────────────────────────────────────────────

const BrandBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $collapsed }) => $collapsed ? "16px 12px" : "16px 16px"};
  height: 60px;
  flex-shrink: 0;
  transition: padding var(--sb-transition);
  border-bottom: 1px solid var(--sb-border);
`;

const LogoImg = styled.img`
  height: 28px;
  width: auto;
  filter: brightness(1.15);
  flex-shrink: 0;
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
  transform: ${({ $collapsed }) => $collapsed ? "scale(0.8)" : "scale(1)"};
  transition: opacity var(--sb-transition), transform var(--sb-transition);
  pointer-events: none;
  user-select: none;
`;

const CollapseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: var(--sb-hover-bg);
  border: 1px solid var(--sb-border);
  color: var(--sb-text-dim);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: var(--sb-active-bg);
    color: var(--sb-text-bright);
    border-color: var(--sb-accent);
  }
`;

const CollapseIcon = styled.span`
  display: inline-block;
  transition: transform var(--sb-transition);
  transform: ${({ $collapsed }) => $collapsed ? "rotate(180deg)" : "rotate(0deg)"};
`;

// ─────────────────────────────────────────────────────────────────────────────
// User card
// ─────────────────────────────────────────────────────────────────────────────

const UserCardWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ $collapsed }) => $collapsed ? "10px 12px" : "10px 14px"};
  margin: 10px ${({ $collapsed }) => $collapsed ? "6px" : "10px"};
  background: var(--sb-surface);
  border: 1px solid var(--sb-border);
  border-radius: 10px;
  cursor: default;
  transition: padding var(--sb-transition), margin var(--sb-transition);
  flex-shrink: 0;
`;

const AvatarRing = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  font-family: 'Geist Mono', monospace;
  letter-spacing: 0.5px;
`;

const UserMeta = styled.div`
  flex: 1;
  overflow: hidden;
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
  width: ${({ $collapsed }) => $collapsed ? 0 : "auto"};
  transition: opacity var(--sb-transition);
  white-space: nowrap;

  .name {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--sb-text-bright);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role {
    font-size: 10.5px;
    color: var(--sb-text-muted);
    margin-top: 1px;
    font-family: 'Geist Mono', monospace;
    letter-spacing: 0.3px;
  }
`;

const PulsingDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: ${pulseDot} 2.5s ease infinite;
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
  transition: opacity var(--sb-transition);
`;

// ─────────────────────────────────────────────────────────────────────────────
// Search trigger
// ─────────────────────────────────────────────────────────────────────────────

const SearchTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 ${({ $collapsed }) => $collapsed ? "6px" : "10px"} 4px;
  padding: ${({ $collapsed }) => $collapsed ? "8px" : "7px 10px"};
  justify-content: ${({ $collapsed }) => $collapsed ? "center" : "flex-start"};
  background: transparent;
  border: 1px solid var(--sb-border);
  border-radius: 8px;
  color: var(--sb-text-dim);
  font-size: 12px;
  font-family: 'Geist', sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  width: ${({ $collapsed }) => $collapsed ? "calc(100% - 12px)" : "calc(100% - 20px)"};
  flex-shrink: 0;

  &:hover {
    background: var(--sb-hover-bg);
    color: var(--sb-text-base);
    border-color: rgba(255,255,255,0.10);
  }

  .search-icon { font-size: 13px; flex-shrink: 0; }
  .search-text {
    flex: 1;
    text-align: left;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    width: ${({ $collapsed }) => $collapsed ? 0 : "auto"};
    overflow: hidden;
    white-space: nowrap;
    transition: opacity var(--sb-transition);
  }
  .kbd {
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 0.6};
    font-size: 10px;
    font-family: 'Geist Mono', monospace;
    background: rgba(255,255,255,0.06);
    padding: 2px 5px;
    border-radius: 4px;
    transition: opacity var(--sb-transition);
    border: 1px solid var(--sb-border);
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Search overlay
// ─────────────────────────────────────────────────────────────────────────────

const SearchOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
  animation: ${fadeSlideIn} 0.15s ease;
`;

const SearchModal = styled.div`
  width: 480px;
  max-width: 90vw;
  background: #0f1828;
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.6);
  overflow: hidden;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 18px;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--sb-border);
  color: #e2e8f0;
  font-size: 15px;
  font-family: 'Geist', sans-serif;
  outline: none;
  caret-color: var(--sb-accent);

  &::placeholder { color: #3d5270; }
`;

const SearchResults = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 4px; }
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
  color: ${({ $active }) => $active ? "var(--sb-text-bright)" : "var(--sb-text-base)"};
  background: ${({ $active }) => $active ? "var(--sb-active-bg)" : "transparent"};

  &:hover { background: var(--sb-hover-bg); color: var(--sb-text-bright); }

  .icon { font-size: 13px; width: 18px; text-align: center; flex-shrink: 0; }
  .label { flex: 1; font-size: 13px; }
  .section { font-size: 10px; color: var(--sb-text-muted); font-family: 'Geist Mono', monospace; }
`;

const SearchEmpty = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--sb-text-muted);
  font-size: 13px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Nav scroll
// ─────────────────────────────────────────────────────────────────────────────

const NavScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0 8px;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #1a2540; border-radius: 4px; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Nav section (collapsible)
// ─────────────────────────────────────────────────────────────────────────────

const SectionWrap = styled.div`
  margin-bottom: var(--sb-section-gap);
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: ${({ $collapsed }) => $collapsed ? "8px 0" : "7px 12px"};
  justify-content: ${({ $collapsed }) => $collapsed ? "center" : "flex-start"};
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--sb-text-muted);
  font-size: 10px;
  font-weight: 700;
  font-family: 'Geist', sans-serif;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  transition: color 0.15s, padding var(--sb-transition);
  user-select: none;

  &:hover { color: var(--sb-text-dim); }

  .s-icon {
    font-size: 13px;
    color: var(--sb-text-muted);
    flex-shrink: 0;
  }

  .s-label {
    flex: 1;
    text-align: left;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    width: ${({ $collapsed }) => $collapsed ? 0 : "auto"};
    overflow: hidden;
    white-space: nowrap;
    transition: opacity var(--sb-transition);
  }

  .s-chevron {
    font-size: 8px;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 0.6};
    transition: transform 0.2s ease, opacity var(--sb-transition);
    transform: ${({ $open }) => $open ? "rotate(0deg)" : "rotate(-90deg)"};
  }
`;

const SectionItems = styled.div`
  overflow: hidden;
  animation: ${fadeSlideIn} 0.18s ease both;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Nav item
// ─────────────────────────────────────────────────────────────────────────────

const NavItemWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: ${({ $collapsed }) => $collapsed ? "9px 0" : "8px 12px"};
  justify-content: ${({ $collapsed }) => $collapsed ? "center" : "flex-start"};
  margin: 1px ${({ $collapsed }) => $collapsed ? "6px" : "8px"};
  border-radius: var(--sb-radius);
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? "500" : "400"};
  color: ${({ $active }) => $active ? "var(--sb-text-bright)" : "var(--sb-text-base)"};
  background: ${({ $active }) => $active ? "var(--sb-active-bg)" : "transparent"};
  cursor: pointer;
  transition: all 0.14s ease;
  user-select: none;

  &:hover {
    background: ${({ $active }) => $active ? "var(--sb-active-bg)" : "var(--sb-hover-bg)"};
    color: var(--sb-text-bright);
  }

  /* Active left accent bar */
  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 25%;
    bottom: 25%;
    width: 2.5px;
    border-radius: 0 3px 3px 0;
    background: ${({ $active }) => $active ? "var(--sb-accent)" : "transparent"};
    transition: background 0.14s;
  }
`;

const ItemIcon = styled.span`
  font-size: 13px;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  color: ${({ $active }) => $active ? "var(--sb-accent)" : "var(--sb-text-muted)"};
  transition: color 0.14s;

  ${NavItemWrap}:hover & {
    color: ${({ $active }) => $active ? "var(--sb-accent)" : "var(--sb-text-dim)"};
  }
`;

const ItemLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
  width: ${({ $collapsed }) => $collapsed ? 0 : "auto"};
  transition: opacity var(--sb-transition);
`;

const ItemShortcut = styled.span`
  font-size: 10px;
  font-family: 'Geist Mono', monospace;
  color: var(--sb-text-muted);
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--sb-border);
  padding: 2px 5px;
  border-radius: 4px;
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
  transition: opacity var(--sb-transition);
`;

/* Tooltip that appears in collapsed mode */
const Tooltip = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  background: #1e2d42;
  color: var(--sb-text-bright);
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
  border: 1px solid var(--sb-border);
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  opacity: 0;
  transition: opacity 0.15s;

  ${NavItemWrap}:hover & {
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: #1e2d42;
    border-left: none;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar bottom
// ─────────────────────────────────────────────────────────────────────────────

const BottomBar = styled.div`
  padding: 10px ${({ $collapsed }) => $collapsed ? "6px" : "8px"};
  border-top: 1px solid var(--sb-border);
  flex-shrink: 0;
  transition: padding var(--sb-transition);
`;

const SignOutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: ${({ $collapsed }) => $collapsed ? "9px 0" : "8px 12px"};
  justify-content: ${({ $collapsed }) => $collapsed ? "center" : "flex-start"};
  width: 100%;
  border-radius: var(--sb-radius);
  background: transparent;
  border: none;
  font-size: 13px;
  font-family: 'Geist', sans-serif;
  color: var(--sb-text-dim);
  cursor: pointer;
  transition: all 0.14s;

  .so-icon { font-size: 13px; width: 18px; text-align: center; flex-shrink: 0; }
  .so-label {
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    width: ${({ $collapsed }) => $collapsed ? 0 : "auto"};
    white-space: nowrap;
    overflow: hidden;
    transition: opacity var(--sb-transition);
  }

  &:hover {
    background: rgba(239, 68, 68, 0.08);
    color: #f87171;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Main content area
// ─────────────────────────────────────────────────────────────────────────────

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

const Topbar = styled.header`
  height: 56px;
  min-height: 56px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  gap: 12px;
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileMenuBtn = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-size: 15px;
    cursor: pointer;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;

  .org { color: #94a3b8; }
  .sep { color: #cbd5e1; }
  .page {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    font-family: 'Geist', sans-serif;
  }
`;

const PageContent = styled.main`
  flex: 1;
  overflow-y: auto;
  background: #f0f4f8;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Single nav item, tooltip shown only when sidebar is collapsed.
 */
const NavItemComp = ({ item, active, collapsed, onClick }) => (
  <NavItemWrap $active={active} $collapsed={collapsed} onClick={onClick}>
    <ItemIcon $active={active}>{item.icon}</ItemIcon>
    <ItemLabel $collapsed={collapsed}>{item.label}</ItemLabel>
    {item.shortcut && !collapsed && <ItemShortcut $collapsed={collapsed}>{item.shortcut}</ItemShortcut>}
    {collapsed && <Tooltip>{item.label}</Tooltip>}
  </NavItemWrap>
);

/**
 * Collapsible section with header + animated items list.
 * In collapsed sidebar mode the header shrinks to just the icon.
 */
const NavSection = ({ section, items, activeTab, setActiveTab, collapsed, defaultOpen = true }) => {
  const hasActive = items.some((i) => i.id === activeTab);
  const [open, setOpen] = useState(defaultOpen || hasActive);

  // Auto-open if the active item is in this section
  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  return (
    <SectionWrap>
      <SectionHeader
        $collapsed={collapsed}
        $open={open}
        onClick={() => !collapsed && setOpen((p) => !p)}
        title={collapsed ? section.label : undefined}
      >
        <span className="s-icon">{section.icon}</span>
        <span className="s-label">{section.label}</span>
        <span className="s-chevron">▾</span>
      </SectionHeader>

      {(open || collapsed) && (
        <SectionItems>
          {items.map((item) => (
            <NavItemComp
              key={item.id}
              item={item}
              active={activeTab === item.id}
              collapsed={collapsed}
              onClick={() => setActiveTab(item.id)}
            />
          ))}
        </SectionItems>
      )}
    </SectionWrap>
  );
};

/**
 * Cmd+K style search overlay.
 */
const SearchOverlayComp = ({ allItems, activeTab, onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const filtered = query.trim()
    ? allItems.filter(
        (i) =>
          i.label.toLowerCase().includes(query.toLowerCase()) ||
          i.section.toLowerCase().includes(query.toLowerCase())
      )
    : allItems;

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setCursor(0); }, [query]);

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === "Enter"  && filtered[cursor]) { onSelect(filtered[cursor].id); onClose(); }
    if (e.key === "Escape") onClose();
  };

  return (
    <SearchOverlay onClick={onClose}>
      <SearchModal onClick={(e) => e.stopPropagation()}>
        <SearchInput
          ref={inputRef}
          placeholder="Search navigation…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKey}
        />
        <SearchResults>
          {filtered.length === 0 ? (
            <SearchEmpty>No results for "{query}"</SearchEmpty>
          ) : (
            filtered.map((item, i) => (
              <SearchResultItem
                key={item.id}
                $active={i === cursor}
                onClick={() => { onSelect(item.id); onClose(); }}
                onMouseEnter={() => setCursor(i)}
              >
                <span className="icon">{item.icon}</span>
                <span className="label">{item.label}</span>
                <span className="section">{item.section}</span>
              </SearchResultItem>
            ))
          )}
        </SearchResults>
      </SearchModal>
    </SearchOverlay>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const NavigationPage = () => {
  const user = auth.currentUser;
  const setAuth = useSetRecoilState(authAtom);
  const navigate = useNavigate();

  // Role flags (unchanged)
  const isKomail   = user?.email === KOMAIL;
  const isVisaOnly = user?.email === VISA_ONLY;
  const isAdmin    = !isKomail && !isVisaOnly && !AGENTS.includes(user?.email);

  const [activeTab, setActiveTab] = useState(isKomail ? "Search Transport" : "Make New Invoice");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((p) => !p);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setAuth(false);
    navigate("/");
  };

  // Ledger items depend on role
  const ledgerItems = isKomail
    ? NAV_SECTIONS.ledgers.items.komail
    : isVisaOnly
    ? NAV_SECTIONS.ledgers.items.visaOnly
    : NAV_SECTIONS.ledgers.items.all;

  const allSearchItems = flattenAllItems();

  const handleNavSelect = (id) => {
    setActiveTab(id);
    setMobileOpen(false);
  };

  return (
    <>
      <GlobalStyle />

      {/* Cmd+K search overlay */}
      {searchOpen && (
        <SearchOverlayComp
          allItems={allSearchItems}
          activeTab={activeTab}
          onSelect={(id) => { setActiveTab(id); setSearchOpen(false); }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      <Layout>

        {/* Mobile backdrop */}
        <MobileOverlay $visible={mobileOpen} onClick={() => setMobileOpen(false)} />

        {/* ── Sidebar ── */}
        <SidebarShell $collapsed={sidebarCollapsed} $mobileOpen={mobileOpen}>

          {/* Brand + collapse */}
          <BrandBar $collapsed={sidebarCollapsed}>
            <LogoImg src={logo} alt="Cross Ocean Travel" $collapsed={sidebarCollapsed} />
            <CollapseBtn
              onClick={() => setSidebarCollapsed((p) => !p)}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <CollapseIcon $collapsed={sidebarCollapsed}>◀</CollapseIcon>
            </CollapseBtn>
          </BrandBar>

          {/* User card */}
          <UserCardWrap $collapsed={sidebarCollapsed}>
            <AvatarRing>{getInitials(user?.email)}</AvatarRing>
            <UserMeta $collapsed={sidebarCollapsed}>
              <div className="name">{getDisplayName(user?.email)}</div>
              <div className="role">{getRole(user?.email)}</div>
            </UserMeta>
            <PulsingDot $collapsed={sidebarCollapsed} />
          </UserCardWrap>

          {/* Search trigger */}
          <SearchTrigger $collapsed={sidebarCollapsed} onClick={() => setSearchOpen(true)}>
            <span className="search-icon">⌕</span>
            <span className="search-text">Quick search…</span>
            <span className="kbd">⌘K</span>
          </SearchTrigger>

          {/* Scrollable nav */}
          <NavScroll>

            {/* Invoices — admin only */}
            {isAdmin && (
              <NavSection
                section={NAV_SECTIONS.invoices}
                items={NAV_SECTIONS.invoices.items}
                activeTab={activeTab}
                setActiveTab={handleNavSelect}
                collapsed={sidebarCollapsed}
                defaultOpen
              />
            )}

            {/* Schedules — everyone */}
            <NavSection
              section={NAV_SECTIONS.schedules}
              items={NAV_SECTIONS.schedules.items}
              activeTab={activeTab}
              setActiveTab={handleNavSelect}
              collapsed={sidebarCollapsed}
              defaultOpen={isKomail}
            />

            {/* Ledgers — admin only */}
            {isAdmin && (
              <NavSection
                section={NAV_SECTIONS.ledgers}
                items={ledgerItems}
                activeTab={activeTab}
                setActiveTab={handleNavSelect}
                collapsed={sidebarCollapsed}
              />
            )}

            {/* Requests — everyone */}
            <NavSection
              section={NAV_SECTIONS.issuanceSubmission}
              items={NAV_SECTIONS.issuanceSubmission.items}
              activeTab={activeTab}
              setActiveTab={handleNavSelect}
              collapsed={sidebarCollapsed}
            />

            {/* Processing — admin only */}
            {isAdmin && (
              <NavSection
                section={NAV_SECTIONS.processing}
                items={NAV_SECTIONS.processing.items}
                activeTab={activeTab}
                setActiveTab={handleNavSelect}
                collapsed={sidebarCollapsed}
              />
            )}

            {/* Operations — admin only */}
            {isAdmin && (
              <NavSection
                section={NAV_SECTIONS.operations}
                items={NAV_SECTIONS.operations.items}
                activeTab={activeTab}
                setActiveTab={handleNavSelect}
                collapsed={sidebarCollapsed}
              />
            )}

          </NavScroll>

          {/* Sign out */}
          <BottomBar $collapsed={sidebarCollapsed}>
            <SignOutBtn $collapsed={sidebarCollapsed} onClick={handleLogout}>
              <span className="so-icon">⎋</span>
              <span className="so-label">Sign out</span>
            </SignOutBtn>
          </BottomBar>

        </SidebarShell>

        {/* ── Content area ── */}
        <ContentArea>
          <Topbar>
            <TopLeft>
              {/* Mobile hamburger */}
              <MobileMenuBtn onClick={() => setMobileOpen((p) => !p)}>☰</MobileMenuBtn>
              <Breadcrumb>
                <span className="org">Cross Ocean Travel</span>
                <span className="sep">›</span>
                <span className="page">{getPageLabel(activeTab)}</span>
              </Breadcrumb>
            </TopLeft>
          </Topbar>

          <PageContent>
            {renderPage(activeTab)}
          </PageContent>
        </ContentArea>

      </Layout>
    </>
  );
};

export default NavigationPage;